import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import c from 'classnames';

import { Container, useDialog, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity, ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { DialogDrawerContext } from '../../../../context/DialogDrawerContext';
import { useQuizContext, type QuizQuestion } from '../../../../context/QuizContext';
import Button from '../../../Button';
import DialogDrawer from '../../../DialogDrawer';
import SwirlIllustrations from '../../../SwirlIllustrations';
import WebformQuizQuestionInfo from '../QuestionInfo';

import './style.scss';

const QUIZ_SUBMISSION_DIALOG_ID = 'quiz-submission-dialog';

type CtaLink = {
  label: string,
  url: string,
};

const WebformQuizSubmission: React.FC = () => {
  const { t } = useTranslation();
  const {
    quizId,
    quizQuestions,
    quizScore,
    resetQuiz,
  } = useQuizContext();
  const dialogDrawer = useDialog();
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState<QuizQuestion>();

  const ctaLink = useSelector<ImmutableRootState<ImmutableEntity<CtaLink>>, CtaLink | undefined>(
    (state) => state.getIn(['webform', quizId, 'info', 'third_party_settings', 'vnl_webform', 'cta_link'])?.toJS(),
  );

  const classNames = c('WebformQuiz__Submission');

  return (
    <div className={classNames}>
      <header className="WebformQuiz__Submission__hero">
        <div className="WebformQuiz__Submission__hero__content">
          <Container>
            <div className="row">
              <div className="col-8 offset-2 col-lg-6 offset-lg-3">
                <div className="WebformQuiz__Submission__hero__content__inner">
                  <h1>{t('webform.quiz.submission.title')}</h1>

                  <WysiwygField
                    property="schema:text"
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    html={t('webform.quiz.submission.score', { correctAnswerCount: quizScore.correct, totalQuestionCount: quizScore.total })}
                  />
                </div>
              </div>
            </div>
          </Container>
        </div>

        <div className="WebformQuiz__Submission__hero__buttons">
          <Container>
            <div className="row">
              <div className="col-lg-6 offset-lg-3">
                <div className="WebformQuiz__Submission__hero__buttons__inner">
                  {ctaLink && (
                    <Button
                      type="primary"
                      href={ctaLink.url}
                    >
                      {ctaLink.label}
                    </Button>
                  )}

                  <Button
                    type="inverted"
                    onClick={resetQuiz}
                  >
                    {t('webform.quiz.submission.retry')}
                  </Button>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <SwirlIllustrations variant="quiz_end" />
      </header>

      <DialogDrawerContext.Provider value={dialogDrawer}>
        {(quizQuestions && quizQuestions.length > 0) && (
          <>
            <main className="WebformQuiz__Submission__answers-list">
              <Container>
                <div className="row">
                  <div className="col-lg-8 offset-lg-2">
                    <div className="WebformQuiz__Submission__answers-list__title">
                      <h2 className="asH4">{t('webform.quiz.submission.answers_list_title')}</h2>
                    </div>

                    <ol>
                      {quizQuestions.map((question) => (
                        <li key={question.id} className="WebformQuiz__Submission__answer">
                          <div className="WebformQuiz__Submission__answer__inner">
                            <div className="WebformQuiz__Submission__answer__title body--regular">
                              {t(`webform.quiz.submission.answer_title_${String(question.isCorrect)}`)}
                            </div>

                            <div className="WebformQuiz__Submission__answer__question-title asH4">
                              {question.title}
                            </div>

                            <DialogDrawer.Button type="link" onClick={() => setCurrentQuizQuestion(question)}>
                              {t('general.read_more')}
                            </DialogDrawer.Button>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </Container>
            </main>

            {currentQuizQuestion && (
              <DialogDrawer id={QUIZ_SUBMISSION_DIALOG_ID}>
                <WebformQuizQuestionInfo question={currentQuizQuestion} />
              </DialogDrawer>
            )}
          </>
        )}
      </DialogDrawerContext.Provider>
    </div>
  );
};

export default WebformQuizSubmission;
